/**
 * QZ Tray bridge — single shared connection to the local print service.
 * Self-signed mode: no cert dialog if QZ Tray is in development/unsigned mode.
 * For production, drop a signed certificate + use qz.security.setCertificatePromise.
 *
 * Public API:
 *   ensureConnected()  → boolean        — returns true if QZ websocket is open
 *   listPrinters()     → string[]       — names known to QZ
 *   printRaw(printerName, tspl)         — sends raw TSPL2 to a USB printer
 */
import qz from "qz-tray";

let connectingPromise = null;

export async function ensureConnected() {
  if (qz.websocket.isActive()) return true;
  if (!connectingPromise) {
    connectingPromise = qz.websocket
      .connect({ retries: 0, delay: 1 })
      .then(() => true)
      .catch(() => false)
      .finally(() => { connectingPromise = null; });
  }
  return connectingPromise;
}

export async function listPrinters() {
  if (!(await ensureConnected())) return [];
  try {
    const printers = await qz.printers.find();
    return Array.isArray(printers) ? printers : [printers];
  } catch { return []; }
}

export async function findDefaultPrinter() {
  const list = await listPrinters();
  // Prefer any printer whose name mentions TSC / TTP-244
  const tsc = list.find((n) => /tsc|ttp[- ]?244/i.test(n));
  return tsc || list[0] || null;
}

export async function printRaw(printerName, tsplString) {
  if (!(await ensureConnected())) {
    throw new Error("QZ Tray is not running. Please start QZ Tray on this workstation.");
  }
  const cfg = qz.configs.create(printerName, { encoding: "ASCII" });
  return qz.print(cfg, [{ type: "raw", format: "plain", data: tsplString }]);
}

/** Bulk USB print — single qz.print() call with N TSPL2 strings. */
export async function printRawBatch(printerName, tsplStrings) {
  if (!(await ensureConnected())) {
    throw new Error("QZ Tray is not running. Please start QZ Tray on this workstation.");
  }
  const cfg = qz.configs.create(printerName, { encoding: "ASCII" });
  const payload = tsplStrings.map((t) => ({ type: "raw", format: "plain", data: t }));
  return qz.print(cfg, payload);
}

export function isQzActive() {
  return qz.websocket.isActive();
}
